import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { useForm, useWatch } from 'react-hook-form'
import MyCheckbox from '../../../../../features/forms/MyCheckbox'
import { toast } from 'react-toastify'
import {
  updateTeamAccount,
  fetchAvailableTools,
  fetchTeamAccounts,
  selectAvailableTools,
  selectAvailableToolsStatus,
  selectTeamAccountsStatus,
} from '../../../settingsSlice'
import {
  TEAM_PAGE_CATEGORIES,
  CANONICAL_TEAM_PAGES,
  mergeAccessPermissionWithCanonical,
  isPagePermissionRow,
  isIntegrationManageRow,
  INTEGRATION_MANAGE_TYPE,
  INTEGRATION_MANAGE_ALL_ID,
  canManageAllTeamIntegrations,
} from '../teamPagePermissions'
import {
  formCheckboxChecked,
  mergeToolPermissions,
  isPayhelmLockedCategory,
  toolPermissionDisplayName,
  toolPermissionSubtitle,
  toolRowMatchesSearch,
} from '../teamMemberPermissionFormUtils'

function CategoryBulkCheckbox({
  inputId,
  checkedCount,
  total,
  onChange,
  disabled = false,
}: {
  inputId: string
  checkedCount: number
  total: number
  onChange: () => void
  disabled?: boolean
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const el = ref.current
    if (el) {
      el.indeterminate = !disabled && total > 0 && checkedCount > 0 && checkedCount < total
    }
  }, [checkedCount, total, disabled])
  if (total === 0) return null
  return (
    <input
      ref={ref}
      id={inputId}
      type="checkbox"
      className={`tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-indigo-600 tw-focus:ring-indigo-400 tw-shrink-0 ${disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
      checked={checkedCount === total}
      onChange={disabled ? undefined : onChange}
      disabled={disabled}
      aria-label="Select or deselect all tools in this category"
    />
  )
}

type Props = {
  email: string
  accessPermission: Array<Record<string, unknown>> | null | undefined
  /** When false, panel does not fetch tools and resets local tool UI state. */
  active: boolean
  /** When true, load and show MCP tool checkboxes (My Team owners/admins; not gated on AI add-on). */
  aiToolsFeatureEnabled: boolean
  /** Inline table expansion (default) vs mobile bottom sheet — sheet layout only used under `md`. */
  layoutVariant?: 'inline' | 'sheet'
  footerVariant?: 'inline' | 'modal'
  /** Owners/admins: single toggle — Manage connections (disconnect/remove any team connection; everyone may still backfill). */
  integrationManagementEnabled?: boolean
  onAfterSave?: () => void
  onDiscard?: () => void
}

const INTEGRATION_MANAGE_ALL_FIELD = 'integration_manage_all'

export default function TeamMemberPermissionsPanel({
  email,
  accessPermission,
  active,
  aiToolsFeatureEnabled,
  layoutVariant = 'inline',
  footerVariant = 'inline',
  integrationManagementEnabled = false,
  onAfterSave,
  onDiscard,
}: Props) {
  const dispatch = useDispatch()
  const availableToolsCatalog = useSelector(selectAvailableTools)
  const availableToolsStatus = useSelector(selectAvailableToolsStatus)
  const teamAccountsStatus = useSelector(selectTeamAccountsStatus)

  useEffect(() => {
    if (teamAccountsStatus === 'idle') {
      dispatch(fetchTeamAccounts())
    }
  }, [teamAccountsStatus, dispatch])

  const accessPermissionSyncKey = useMemo(
    () => JSON.stringify(Array.isArray(accessPermission) ? accessPermission : []),
    [accessPermission]
  )

  const toolsCatalogSyncKey = useMemo(
    () =>
      Array.isArray(availableToolsCatalog)
        ? availableToolsCatalog.map((t: { id?: string }) => String(t.id || '')).join('\0')
        : '',
    [availableToolsCatalog]
  )

  const mergedAccess = useMemo(() => mergeAccessPermissionWithCanonical(accessPermission), [accessPermission])

  const pagePermissions = useMemo(
    () => mergedAccess.filter(isPagePermissionRow) as Array<{ id: string; label: string; checked?: boolean }>,
    [mergedAccess]
  )

  /** Team members never edit owner-only pages in this UI. */
  const memberPagePermissions = useMemo(
    () =>
      pagePermissions.filter((p) => {
        const def = CANONICAL_TEAM_PAGES.find((c) => c.id === p.id)
        return def && !def.ownerOnly
      }),
    [pagePermissions]
  )

  const pagesByCategory = useMemo(() => {
    const map = new Map<string, typeof memberPagePermissions>()
    for (const cat of TEAM_PAGE_CATEGORIES) {
      map.set(cat.key, [])
    }
    for (const row of memberPagePermissions) {
      const def = CANONICAL_TEAM_PAGES.find((c) => c.id === row.id)
      if (!def) continue
      const list = map.get(def.category) || []
      list.push(row)
      map.set(def.category, list)
    }
    return map
  }, [memberPagePermissions])

  const mergedToolPermissions = useMemo(
    () => mergeToolPermissions(availableToolsCatalog, mergedAccess),
    [availableToolsCatalog, mergedAccess]
  )

  const [toolsSearchQuery, setToolsSearchQuery] = useState('')

  const filteredToolPermissions = useMemo(
    () => mergedToolPermissions.filter((row) => toolRowMatchesSearch(row, toolsSearchQuery)),
    [mergedToolPermissions, toolsSearchQuery]
  )

  const groupedToolPermissions = useMemo(() => {
    const map = new Map<string, { category: string; categoryLabel: string; tools: typeof mergedToolPermissions }>()
    for (const row of filteredToolPermissions) {
      const cat = row.category || 'other'
      const categoryLabel = row.categoryLabel || 'Other'
      if (!map.has(cat)) {
        map.set(cat, { category: cat, categoryLabel, tools: [] })
      }
      map.get(cat)!.tools.push(row)
    }
    const groups = Array.from(map.values())
    groups.sort((a, b) => {
      if (a.category === 'other') return 1
      if (b.category === 'other') return -1
      return (a.categoryLabel || '').localeCompare(b.categoryLabel || '', undefined, { sensitivity: 'base' })
    })
    return groups
  }, [filteredToolPermissions])

  const showAiChatToolsSection =
    aiToolsFeatureEnabled &&
    Array.isArray(availableToolsCatalog) &&
    availableToolsCatalog.length > 0

  const { register, handleSubmit, setValue, reset, control } = useForm()
  const formValues = useWatch({ control }) || {}

  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set<string>())
  const isCategoryExpanded = (categoryKey: string) => !collapsedCategories.has(categoryKey)
  const toggleCategoryExpanded = (categoryKey: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryKey)) next.delete(categoryKey)
      else next.add(categoryKey)
      return next
    })
  }

  const catalogReady =
    Array.isArray(availableToolsCatalog) && availableToolsCatalog.length > 0

  useEffect(() => {
    if (!active) {
      setToolsSearchQuery('')
      return
    }
    if (!aiToolsFeatureEnabled) return
    if (catalogReady) return
    if (availableToolsStatus === 'loading') return
    dispatch(fetchAvailableTools())
  }, [active, aiToolsFeatureEnabled, catalogReady, availableToolsStatus, dispatch])

  const collapseAccordionInitializedRef = useRef(false)
  useEffect(() => {
    if (!active) {
      collapseAccordionInitializedRef.current = false
      return
    }
    if (!showAiChatToolsSection || mergedToolPermissions.length === 0) return
    if (collapseAccordionInitializedRef.current) return
    collapseAccordionInitializedRef.current = true
    const cats = mergedToolPermissions.map((r) => r.category || 'other')
    setCollapsedCategories(new Set(cats))
  }, [active, showAiChatToolsSection, mergedToolPermissions])

  useEffect(() => {
    if (!active) return
    const defaults: Record<string, boolean> = {}
    pagePermissions.forEach(({ id, checked }) => {
      defaults[id] = !!checked
    })
    if (showAiChatToolsSection) {
      mergedToolPermissions.forEach(({ id, checked, category }) => {
        defaults[id] = isPayhelmLockedCategory(category as string) ? true : !!checked
      })
    }
    if (integrationManagementEnabled) {
      defaults[INTEGRATION_MANAGE_ALL_FIELD] = canManageAllTeamIntegrations(accessPermission)
    }
    reset(defaults)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit unstable row refs; *SyncKey deps avoid reset() on every integrations refetch (was clearing checkbox clicks).
  }, [
    active,
    email,
    accessPermissionSyncKey,
    toolsCatalogSyncKey,
    integrationManagementEnabled,
    showAiChatToolsSection,
    reset,
  ])

  const setAllTools = (checked: boolean) => {
    mergedToolPermissions.forEach(({ id, category }) => {
      if (isPayhelmLockedCategory(category as string)) return
      setValue(id, checked)
    })
  }

  const getCategoryCheckedTotal = (categoryKey: string) => {
    const rows = mergedToolPermissions.filter((r) => (r.category || 'other') === categoryKey)
    const checked = rows.filter((r) => formCheckboxChecked(formValues, r.id)).length
    return { checked, total: rows.length }
  }

  const toggleCategoryTools = (categoryKey: string) => {
    if (isPayhelmLockedCategory(categoryKey)) return
    const rows = mergedToolPermissions.filter((r) => (r.category || 'other') === categoryKey)
    if (rows.length === 0) return
    const checkedCount = rows.filter((r) => formCheckboxChecked(formValues, r.id)).length
    const newVal = checkedCount < rows.length
    rows.forEach((r) => setValue(r.id, newVal))
  }

  const onSubmit = async (values: Record<string, unknown>) => {
    const pagePayload = pagePermissions.map((item) => ({
      ...item,
      checked: formCheckboxChecked(values, item.id),
    }))
    const savedToolRows = (mergedAccess || []).filter((p) => p && p.type === 'mcp_tool')
    const toolPayload = showAiChatToolsSection
      ? mergedToolPermissions.map((item) => ({
          ...item,
          checked: isPayhelmLockedCategory(item.category as string)
            ? true
            : formCheckboxChecked(values, item.id),
        }))
      : savedToolRows
    const savedIntegrationRows = (mergedAccess || []).filter(isIntegrationManageRow)
    const integrationPayload = integrationManagementEnabled
      ? [
          {
            type: INTEGRATION_MANAGE_TYPE,
            id: INTEGRATION_MANAGE_ALL_ID,
            label: 'Manage connections',
            checked: formCheckboxChecked(values, INTEGRATION_MANAGE_ALL_FIELD),
          },
        ]
      : savedIntegrationRows
    const updatePayload = [...pagePayload, ...toolPayload, ...integrationPayload]

    try {
      await dispatch(updateTeamAccount({ invited: email, accessPermission: updatePayload })).unwrap()
      toast.success(`Access updated for ${email}`)
      onAfterSave?.()
    } catch (error) {
      console.error('TeamMemberPermissionsPanel save', error)
      toast.error(`Could not update access for ${email}`)
    }
  }

  const handleDiscard = () => {
    const defaults: Record<string, boolean> = {}
    pagePermissions.forEach(({ id, checked }) => {
      defaults[id] = !!checked
    })
    if (showAiChatToolsSection) {
      mergedToolPermissions.forEach(({ id, checked, category }) => {
        defaults[id] = isPayhelmLockedCategory(category as string) ? true : !!checked
      })
    }
    if (integrationManagementEnabled) {
      defaults[INTEGRATION_MANAGE_ALL_FIELD] = canManageAllTeamIntegrations(accessPermission)
    }
    reset(defaults)
    onDiscard?.()
  }

  const hasAnyPagePermissions =
    TEAM_PAGE_CATEGORIES.some((c) => (pagesByCategory.get(c.key) || []).length > 0)

  const isSheet = layoutVariant === 'sheet'

  const pageSection =
    hasAnyPagePermissions ? (
      <div
        className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 ${isSheet ? 'tw-mb-4' : 'tw-mb-5'}`}
      >
        <div className="tw-mb-3 tw-space-y-1">
          <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-slate-800">Page Permissions</h3>
          <p className="tw-mb-0 tw-text-xs tw-text-slate-500">
            Matches navigation and account areas: Dashboard, Traffic, AI Chat, Reports, Integrations, Inventory,
            Shipping, My Team, Preferences, and My Plan.
          </p>
        </div>
        {TEAM_PAGE_CATEGORIES.map((cat) => {
          const rows = pagesByCategory.get(cat.key) || []
          if (rows.length === 0) return null
          return (
            <div key={cat.key}>
              <h4 className="tw-mb-3 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-slate-500 tw-uppercase">
                {cat.title}
              </h4>
              <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
                {rows.map(({ id, label }) => (
                  <div
                    key={id}
                    className={`tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-border tw-border-slate-200/80 tw-bg-slate-50/50 tw-px-3 ${
                      isSheet ? 'tw-min-h-[44px] tw-py-2' : 'tw-py-2'
                    }`}
                  >
                    <label htmlFor={`team-${email}-${id}`} className="tw-mb-0 tw-text-sm tw-text-slate-800">
                      {label}
                    </label>
                    <input
                      id={`team-${email}-${id}`}
                      type="checkbox"
                      className="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-gray-300 tw-text-indigo-600"
                      {...register(id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    ) : null

  const integrationSection = integrationManagementEnabled ? (
    <div
      className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 ${isSheet ? 'tw-mb-4' : 'tw-mb-5'}`}
    >
      <div className="tw-mb-3 tw-space-y-1">
        <h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-slate-800">Integration management</h3>
        <p className="tw-mb-0 tw-text-xs tw-text-slate-500">
          Everyone can run backfills from the Integrations page. When enabled, this member may disconnect or remove any team
          connection; otherwise they may only disconnect integrations they connected themselves.
        </p>
      </div>
      <div
        className={`tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-border tw-border-slate-200/80 tw-bg-slate-50/50 tw-px-3 ${
          isSheet ? 'tw-min-h-[44px] tw-py-2' : 'tw-py-2'
        }`}
      >
        <label htmlFor={`team-${email}-${INTEGRATION_MANAGE_ALL_FIELD}`} className="tw-mb-0 tw-text-sm tw-text-slate-800">
          Manage connections
        </label>
        <input
          id={`team-${email}-${INTEGRATION_MANAGE_ALL_FIELD}`}
          type="checkbox"
          className="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-gray-300 tw-text-indigo-600"
          {...register(INTEGRATION_MANAGE_ALL_FIELD)}
        />
      </div>
    </div>
  ) : null

  const aiSection = showAiChatToolsSection ? (
    <div className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 ${isSheet ? 'tw-mb-2' : 'tw-mb-5'}`}>
      <div className="tw-mb-3 tw-space-y-2">
        <div
          className={
            isSheet
              ? 'tw-flex tw-w-full tw-flex-col tw-gap-3'
              : 'tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2'
          }
        >
          <div className="tw-space-y-0.5">
            <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-800">AI Chat tools</span>
            <span className="tw-block tw-text-xs tw-text-slate-500">
              Fine-grained tools the agent may use for this member.
            </span>
          </div>
          <div
            className={`tw-flex tw-flex-shrink-0 tw-flex-wrap tw-gap-2 ${isSheet ? 'tw-w-full tw-justify-end' : ''}`}
          >
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              className={isSheet ? 'tw-min-h-[44px] tw-px-4' : ''}
              onClick={() => setAllTools(true)}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              className={isSheet ? 'tw-min-h-[44px] tw-px-4' : ''}
              onClick={() => setAllTools(false)}
            >
              Deselect all
            </Button>
          </div>
        </div>
        <Form.Control
          type="search"
          size="sm"
          value={toolsSearchQuery}
          onChange={(e) => setToolsSearchQuery(e.target.value)}
          placeholder="Search tools…"
          aria-label="Search AI Chat tools"
          className={isSheet ? 'tw-w-full tw-text-base' : ''}
        />
        <p className="tw-mb-0 tw-text-xs tw-text-slate-500">
          Showing {filteredToolPermissions.length} of {mergedToolPermissions.length} tools
        </p>
      </div>
      <div
        className={
          isSheet ? 'tw-space-y-2 tw-pr-1' : 'tw-max-h-64 tw-space-y-2 tw-overflow-y-auto tw-pr-1'
        }
      >
        {filteredToolPermissions.length === 0 ? (
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">No tools match your search.</p>
        ) : null}
        {groupedToolPermissions.map(({ category, categoryLabel, tools: groupTools }) => {
          const { checked: catChecked, total: catTotal } = getCategoryCheckedTotal(category)
          const expanded = isCategoryExpanded(category)
          const headerId = `ai-tools-cat-${email}-${category}`
          const categoryLocked = isPayhelmLockedCategory(category)
          return (
            <div
              key={category}
              className="tw-overflow-hidden tw-rounded-md tw-border tw-border-slate-200 tw-bg-slate-50/50"
            >
              <div
                className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-bg-slate-100/80 tw-px-2 ${
                  isSheet ? 'tw-min-h-[44px] tw-py-2' : 'tw-py-1.5'
                }`}
              >
                <CategoryBulkCheckbox
                  inputId={`${headerId}-bulk`}
                  checkedCount={catChecked}
                  total={catTotal}
                  onChange={() => toggleCategoryTools(category)}
                  disabled={categoryLocked}
                />
                <button
                  type="button"
                  className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-sm tw-font-semibold tw-text-slate-800"
                  onClick={() => toggleCategoryExpanded(category)}
                  aria-expanded={expanded}
                >
                  <span className="tw-truncate">{categoryLabel}</span>
                  <span className="tw-shrink-0 tw-text-xs tw-text-slate-500">
                    {catChecked}/{catTotal} · {expanded ? '▾' : '▸'}
                  </span>
                </button>
              </div>
              {expanded ? (
                <div className="tw-space-y-1 tw-border-t tw-border-slate-100 tw-p-2">
                  {groupTools.map((row) => {
                    const { id, label, description, category: rowCategory } = row
                    const title = toolPermissionDisplayName(id)
                    const subtitle = toolPermissionSubtitle({ id, label, description })
                    const rowLocked = isPayhelmLockedCategory(rowCategory as string)
                    return (
                      <div
                        key={id}
                        className={`tw-rounded tw-border tw-border-slate-100 tw-bg-white tw-p-2 ${
                          isSheet ? 'tw-border-l-[3px] tw-border-l-indigo-400 tw-pl-3' : ''
                        }`}
                      >
                        <div className={isSheet ? 'tw-flex tw-min-h-[44px] tw-items-center' : ''}>
                          <MyCheckbox
                            register={register}
                            name={id}
                            disabled={rowLocked}
                            label={
                            <span className="tw-font-mono tw-text-xs tw-font-semibold tw-text-slate-900">
                              {title}
                            </span>
                          }
                          content={
                            subtitle ? (
                              <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-slate-600">{subtitle}</p>
                            ) : null
                          }
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  ) : null

  const footer = isSheet ? (
    <div className="tw-mt-auto tw-shrink-0 tw-border-t tw-border-slate-200 tw-bg-white tw-pt-3 tw-pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-2">
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          className="tw-order-1 tw-w-full tw-min-h-[44px] tw-border-slate-200 tw-bg-transparent tw-text-slate-700 hover:tw-bg-slate-50"
          onClick={handleDiscard}
        >
          {footerVariant === 'modal' ? 'Cancel' : 'Discard'}
        </Button>
        <Button variant="primary" size="sm" type="submit" className="tw-order-2 tw-w-full tw-min-h-[44px]">
          {footerVariant === 'modal' ? 'Update access permissions' : 'Save changes'}
        </Button>
      </div>
    </div>
  ) : (
    <div className="tw-mt-6 tw-flex tw-items-center tw-justify-end tw-gap-2">
      <Button type="button" variant="outline-secondary" size="sm" onClick={handleDiscard}>
        {footerVariant === 'modal' ? 'Cancel' : 'Discard'}
      </Button>
      <Button variant="primary" size="sm" type="submit">
        {footerVariant === 'modal' ? 'Update access permissions' : 'Save changes'}
      </Button>
    </div>
  )

  return (
    <div
      className={
        isSheet
          ? 'tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-bg-white'
          : 'tw-border-t tw-border-slate-100 tw-bg-slate-50/80 tw-px-4 tw-py-4'
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={isSheet ? 'tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col' : 'tw-w-full'}
      >
        {isSheet ? (
          <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-y-contain tw-pb-2">
            {pageSection}
            {integrationSection}
            {aiSection}
          </div>
        ) : (
          <>
            {pageSection}
            {integrationSection}
            {aiSection}
          </>
        )}
        {footer}
      </form>
    </div>
  )
}
